import { isPremiumChatLocked } from '../utils/premiumChatAccess';
import { useModels } from '../contexts/ModelContext';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { auth, firestore } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import uuid from 'react-native-uuid';

import ParticleBackground from '../components/background/ParticleBackground';
import FeatureAnnouncement from '../components/ui/FeatureAnnouncement';
import MemoryConsentModal from '../components/memory/MemoryConsentModal';
import LoadingScreen from '../components/layout/LoadingScreen';
import AppLayout from '../components/layout/AppLayout';
import MobileLayout from '../components/layout/MobileLayout';
import DesktopLayout from '../components/layout/DesktopLayout';
import { getFeatureAnnouncementConfig } from '../components/ui/FeatureAnnouncementConfig';
import { buildApiUrl } from '../config/env';
import { createChatOnServer, updateChatTopicOnServer } from '../utils/chatApi';
import {
  getMemorySettings,
  saveProfileMemories,
  updateMemorySettings,
} from '../utils/memoryApi';
import { captureAnalyticsEvent } from '../analytics/posthog';

// Custom hooks
import { useSubscriptionManager } from '../hooks/chat/useSubscriptionManager';
import { useResizeHandlers } from '../hooks/ui/useResizeHandlers';
import { useChatState } from '../hooks/chat/useChatState';
import { useAuthAndUser } from '../hooks/shared/useAuthAndUser';
import { useArtifactState } from '../hooks/ui/useArtifactState';
import { useLocalStorageSync } from '../hooks/shared/useLocalStorageSync';
import { useRealtimeVoice } from '../hooks/shared/useRealtimeVoice';
import { getChatProjectId, useProjects } from '../hooks/projects/useProjects';
import { updateChatSearchIndexForMessage } from '../utils/chatSearchIndex';

const DEFAULT_CHAT_TOPIC = 'Untitled Chat';
const MEMORY_CONSENT_VERSION = 'memory-v2';

const MainChatInterface = () => {
  // Custom hooks
  const {
    hasSubscription,
    hasMaxSubscription,
    hasBasicSubscription,
    subscriptionSources,
    isCheckingSubscription,
    initializeSubscriptionCheck,
    subscriptionStatus,
    isActivating,
    checkSubscription,
  } = useSubscriptionManager();
  const { isMobile, isSidebarOpen, setIsSidebarOpen, chatWidth, artifactWidth, toggleSidebar, handleResize } = useResizeHandlers();
  const {
    chatTitle, chats, selectedChat, messages, secondaryMessages, isDualMode, secondaryModel,
    selectedModel, selectedModelName, messageLimit, reasoningStream, inputText, showSettings, isLoadingMessages,
    setIsDualMode, setSecondaryModel, setInputText, handleReasoningStreamUpdate, handleChatSelect,
    handleNewChat, updateChatTitle, handleModelChange, handlePinnedModelChange, handleMessageSent,
    handleSecondaryMessageSent, handleSettingsClick, handleBackToChat, handleQuestionClick,
    startEditingMessage, cancelEditingMessage, editingMessage, handleEditAndTruncate
  } = useChatState();
  const { models } = useModels();
  const isChatLockedRef = useRef(false);
  isChatLockedRef.current = isPremiumChatLocked(selectedChat, [
    ...(messages[selectedChat?.id || 'new'] || []),
    ...(secondaryMessages[selectedChat?.id || 'new'] || []),
  ], models, hasSubscription, [selectedModel, ...(isDualMode ? [secondaryModel] : [])]);
  const { userName, userEmail, handleLogout, handleUpgrade } = useAuthAndUser();
  const { currentArtifact, isArtifactExpanded, handleArtifactOpen, handleArtifactClose, handleToggleArtifactExpanded } = useArtifactState(selectedChat, messages);
  const { particlesEnabled, setParticlesEnabled, chatSidebarViewMode, setChatSidebarViewMode, syncSidebarState } = useLocalStorageSync();
  const {
    activeProject,
    activeProjectId,
    createProject,
    deleteProject,
    isLoadingProjects,
    moveChatToProject,
    projectError,
    projects,
    selectProject,
    updateProject,
  } = useProjects();
  const persistedRealtimeTurnIdsRef = useRef(new Set());
  const realtimePersistenceChainRef = useRef(Promise.resolve());
  const realtimeChatIdRef = useRef(selectedChat?.id || null);
  const voiceTopicGeneratedChatsRef = useRef(new Set());
  const latestVoiceUserTranscriptByChatRef = useRef(new Map());
  const activeChatSessionRef = useRef(null);
  const [memoryConsentOpen, setMemoryConsentOpen] = React.useState(false);
  const [memoryConsentStep, setMemoryConsentStep] = React.useState('intro');
  const [memorySetupWorking, setMemorySetupWorking] = React.useState(false);
  const [memorySetupProgress, setMemorySetupProgress] = React.useState('');
  const [memorySetupError, setMemorySetupError] = React.useState('');
  const [memoryNickname, setMemoryNickname] = React.useState('');
  const [memoryOccupation, setMemoryOccupation] = React.useState('');
  const memoryProfileStepRef = useRef(false);

  // Initialize subscription check
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      initializeSubscriptionCheck(user);
    }
  }, [initializeSubscriptionCheck]);

  useEffect(() => {
    let isMounted = true;

    const checkConsent = async (user) => {
      if (!user) {
        setMemoryConsentOpen(false);
        return;
      }

      try {
        const payload = await getMemorySettings();
        const settings = payload.settings || {};
        if (!isMounted || auth.currentUser?.uid !== user.uid) return;
        if (memoryProfileStepRef.current && settings.prompted === true) return;

        setMemoryNickname(settings.nickname || '');
        setMemoryOccupation(settings.occupation || '');
        setMemoryConsentStep('intro');
        setMemoryConsentOpen(settings.consentRequired === true || settings.prompted !== true);

      } catch (error) {
        console.error('Failed to load memory settings:', error);
      }
    };
    const unsubscribe = onAuthStateChanged(auth, checkConsent);
    const onFocus = () => checkConsent(auth.currentUser);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const handleEnableMemory = useCallback(async () => {
    setMemorySetupWorking(true);
    setMemorySetupError('');
    setMemorySetupProgress('Enabling memory...');

    try {
      await updateMemorySettings({
        enabled: true,
        savedMemoriesEnabled: true,
        chatHistoryEnabled: true,
        memoryConsentVersion: MEMORY_CONSENT_VERSION,
      });
      setMemorySetupProgress('Memory is on. Past chats will index in the background.');
      memoryProfileStepRef.current = true;
      setMemoryConsentStep('profile');
    } catch (error) {
      setMemorySetupError(error.message || 'Failed to set up memory');
    } finally {
      setMemorySetupWorking(false);
    }
  }, []);

  const closeMemoryConsent = useCallback(() => {
    memoryProfileStepRef.current = false;
    setMemoryConsentOpen(false);
    setMemoryConsentStep('intro');
    setMemorySetupProgress('');
    setMemorySetupError('');
  }, []);

  const handleDisableMemory = useCallback(async () => {
    if (memorySetupWorking) return;
    setMemorySetupError('');
    try {
      await updateMemorySettings({
        enabled: false,
        savedMemoriesEnabled: false,
        chatHistoryEnabled: false,
        memoryConsentVersion: MEMORY_CONSENT_VERSION,
      });
    } catch (error) {
      console.error('Failed to disable memory:', error);
    } finally {
      closeMemoryConsent();
    }
  }, [closeMemoryConsent, memorySetupWorking]);

  const handleSaveMemoryProfile = useCallback(async () => {
    if (memorySetupWorking) return;
    setMemorySetupWorking(true);
    setMemorySetupError('');

    try {
      await saveProfileMemories({
        nickname: memoryNickname.trim(),
        occupation: memoryOccupation.trim(),
      });
      closeMemoryConsent();
    } catch (error) {
      setMemorySetupError(error.message || 'Failed to save memory details');
    } finally {
      setMemorySetupWorking(false);
    }
  }, [closeMemoryConsent, memoryNickname, memoryOccupation, memorySetupWorking]);

  // Sync sidebar state to localStorage
  useEffect(() => {
    syncSidebarState(isSidebarOpen);
  }, [isSidebarOpen, syncSidebarState]);

  useEffect(() => {
    realtimeChatIdRef.current = selectedChat?.id || null;
  }, [selectedChat?.id]);

  useEffect(() => {
    if (selectedChat?.id && selectedChat?.topic && selectedChat.topic !== DEFAULT_CHAT_TOPIC) {
      voiceTopicGeneratedChatsRef.current.add(selectedChat.id);
    }
  }, [selectedChat?.id, selectedChat?.topic]);

  const flushActiveChatDuration = useCallback((reason) => {
    const activeSession = activeChatSessionRef.current;
    if (!activeSession?.chatId || !activeSession.startedAt) {
      return;
    }

    const durationSeconds = (Date.now() - activeSession.startedAt) / 1000;
    if (durationSeconds >= 1) {
      captureAnalyticsEvent('chat_time_spent', {
        chat_id: activeSession.chatId,
        chat_topic: activeSession.topic || null,
        model_name: activeSession.modelName || null,
        duration_seconds: Number(durationSeconds.toFixed(2)),
        end_reason: reason,
      });
    }

    activeChatSessionRef.current = null;
  }, []);

  useEffect(() => {
    const nextChatId = selectedChat?.id || null;
    const currentSession = activeChatSessionRef.current;

    if (currentSession?.chatId && currentSession.chatId !== nextChatId) {
      flushActiveChatDuration('chat_switched');
    }

    if (nextChatId && (!currentSession || currentSession.chatId !== nextChatId)) {
      activeChatSessionRef.current = {
        chatId: nextChatId,
        topic: selectedChat?.topic || null,
        modelName: selectedChat?.model || selectedModelName || null,
        startedAt: Date.now(),
      };
      return;
    }

    if (!nextChatId) {
      activeChatSessionRef.current = null;
    }
  }, [flushActiveChatDuration, selectedChat?.id, selectedChat?.model, selectedChat?.topic, selectedModelName]);

  useEffect(() => {
    const activeSession = activeChatSessionRef.current;
    if (!activeSession || activeSession.chatId !== selectedChat?.id) {
      return;
    }

    activeChatSessionRef.current = {
      ...activeSession,
      topic: selectedChat?.topic || null,
      modelName: selectedChat?.model || selectedModelName || null,
    };
  }, [selectedChat?.id, selectedChat?.model, selectedChat?.topic, selectedModelName]);

  useEffect(() => {
    const handlePageHide = () => {
      flushActiveChatDuration('page_hidden');
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      flushActiveChatDuration('component_unmount');
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [flushActiveChatDuration]);

  const maybeGenerateVoiceTopic = useCallback(
    async ({ chatId, assistantTranscript }) => {
      if (!chatId || !assistantTranscript?.trim()) {
        return;
      }
      if (voiceTopicGeneratedChatsRef.current.has(chatId)) {
        return;
      }

      const chat = chats.find((item) => item.id === chatId);
      const existingTopic = chat?.topic || (selectedChat?.id === chatId ? selectedChat.topic : null);
      if (existingTopic && existingTopic !== DEFAULT_CHAT_TOPIC) {
        voiceTopicGeneratedChatsRef.current.add(chatId);
        return;
      }

      const userTranscript = latestVoiceUserTranscriptByChatRef.current.get(chatId) || '';
      const topicPrompt = `
        Based on the following voice conversation, generate a concise topic of 4-5 words maximum and 30 letters or less:
        User: ${userTranscript}
        AI: ${assistantTranscript}
        Topic:
      `;

      const authToken = await auth.currentUser.getIdToken();
      const topicResponse = await fetch(buildApiUrl('/generate-topic'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ prompt: topicPrompt }),
      });

      if (!topicResponse.ok) {
        throw new Error('Failed to generate voice topic');
      }

      const topicPayload = await topicResponse.json();
      const generatedTopic = (topicPayload?.topic || DEFAULT_CHAT_TOPIC).trim();
      if (!generatedTopic) {
        return;
      }

      await updateChatTopicOnServer(chatId, generatedTopic);
      voiceTopicGeneratedChatsRef.current.add(chatId);
      updateChatTitle(generatedTopic);
    },
    [chats, selectedChat?.id, selectedChat?.topic, updateChatTitle]
  );

  const appendRealtimeTranscriptToHistory = useCallback(
    ({ role, transcript, eventId }) => {
      if (!role || typeof transcript !== 'string') {
        return;
      }

      const trimmedTranscript = transcript.trim();
      if (!trimmedTranscript) {
        return;
      }

      const dedupeKey = eventId || `${role}:${trimmedTranscript}`;
      if (persistedRealtimeTurnIdsRef.current.has(dedupeKey)) {
        return;
      }
      persistedRealtimeTurnIdsRef.current.add(dedupeKey);
      if (persistedRealtimeTurnIdsRef.current.size > 1000) {
        const recentIds = Array.from(persistedRealtimeTurnIdsRef.current).slice(-500);
        persistedRealtimeTurnIdsRef.current = new Set(recentIds);
      }

      const persistTurn = async () => {
        let chatId = realtimeChatIdRef.current;
        if (isChatLockedRef.current) return;
        if (!chatId) {
          const chatResponse = await createChatOnServer({
            modelName: selectedChat?.model || selectedModelName || 'GPT 4o mini',
            multiModel: false,
            projectId: getChatProjectId(selectedChat) || activeProjectId,
          });
          chatId = chatResponse.chatId;
          realtimeChatIdRef.current = chatId;
        }

        const message = {
          id: uuid.v4(),
          text: trimmedTranscript,
          role,
          streaming: false,
          pending: false,
          ...(role === 'assistant'
            ? { modelUsed: selectedChat?.model || selectedModelName }
            : {}),
        };

        handleMessageSent(message, chatId);

        const messageRef = doc(firestore, 'chats', chatId, 'messages', message.id);
        const chatRef = doc(firestore, 'chats', chatId);

        await setDoc(messageRef, {
          id: message.id,
          role: message.role,
          text: message.text,
          createdAt: serverTimestamp(),
          ...(message.modelUsed ? { modelUsed: message.modelUsed } : {}),
        });
        await updateChatSearchIndexForMessage({
          db: firestore,
          chatRef,
          message,
        });

        if (role === 'user') {
          latestVoiceUserTranscriptByChatRef.current.set(chatId, trimmedTranscript);
          return;
        }

        await maybeGenerateVoiceTopic({
          chatId,
          assistantTranscript: trimmedTranscript,
        });
      };

      realtimePersistenceChainRef.current = realtimePersistenceChainRef.current
        .then(persistTurn)
        .catch((persistError) => {
          console.error('Failed to persist realtime transcript message:', persistError);
        });
    },
    [activeProjectId, handleMessageSent, maybeGenerateVoiceTopic, selectedChat, selectedModelName]
  );

  const handleRealtimeUserTranscriptFinal = useCallback(
    ({ transcript, eventId }) => {
      appendRealtimeTranscriptToHistory({
        role: 'user',
        transcript,
        eventId,
      });
    },
    [appendRealtimeTranscriptToHistory]
  );

  const handleRealtimeAssistantTranscriptFinal = useCallback(
    ({ transcript, eventId }) => {
      appendRealtimeTranscriptToHistory({
        role: 'assistant',
        transcript,
        eventId,
      });
    },
    [appendRealtimeTranscriptToHistory]
  );

  const realtimeVoice = useRealtimeVoice({
    onUserTranscriptFinal: handleRealtimeUserTranscriptFinal,
    onAssistantTranscriptFinal: handleRealtimeAssistantTranscriptFinal,
  });

  // Wrapper functions to pass additional parameters to hooks
  const wrappedHandleChatSelect = (chatId) => {
    const chat = chats.find((item) => item.id === chatId);
    selectProject(getChatProjectId(chat));
    handleChatSelect(chatId, isMobile, setIsSidebarOpen);
  };
  const wrappedHandleNewChat = () => {
    selectProject(null);
    handleNewChat(isMobile, setIsSidebarOpen);
  };
  const wrappedHandleProjectSelect = (projectId, options = {}) => {
    const nextProjectId = projectId || null;
    const currentProjectId = getChatProjectId(selectedChat);
    selectProject(nextProjectId);
    if (
      selectedChat?.id &&
      (currentProjectId !== nextProjectId || options.openWorkspaceHome)
    ) {
      handleNewChat(isMobile, setIsSidebarOpen);
    }
  };
  const wrappedMoveChatToProject = async ({ chatId, projectId }) => {
    const result = await moveChatToProject({ chatId, projectId });
    if (selectedChat?.id === chatId && activeProjectId && projectId !== activeProjectId) {
      handleNewChat(isMobile, setIsSidebarOpen);
    }
    return result;
  };
  const wrappedHandleSettingsClick = () => handleSettingsClick(isMobile, setIsSidebarOpen);
  const wrappedHandleArtifactOpen = (artifact) => {
    handleArtifactOpen(artifact, isMobile);
    if (!isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Determine if particles should animate
  const shouldAnimateParticles = useMemo(() => {
    // Only animate when no chat is selected and no messages, and particles are enabled
    return particlesEnabled && !selectedChat && (!messages.new || messages.new.length === 0);
  }, [selectedChat, messages, particlesEnabled]);

  return (
    <AppLayout>
      <MemoryConsentModal
        isOpen={memoryConsentOpen}
        isWorking={memorySetupWorking}
        step={memoryConsentStep}
        progressText={memorySetupProgress}
        error={memorySetupError}
        nickname={memoryNickname}
        occupation={memoryOccupation}
        onEnable={handleEnableMemory}
        onDisable={handleDisableMemory}
        onNicknameChange={setMemoryNickname}
        onOccupationChange={setMemoryOccupation}
        onSaveProfile={handleSaveMemoryProfile}
        onSkipProfile={closeMemoryConsent}
      />
      {!isCheckingSubscription && isActivating && subscriptionStatus !== 'unknown' && (
        <div role="status" style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10000, padding: '12px 18px', borderRadius: 12, background: 'var(--background-color, #fff)', color: 'var(--text-color, #222)', boxShadow: '0 2px 12px #0002' }}>
          Checking subscription activation. You don’t need to purchase again. <button onClick={checkSubscription}>Check again</button>
        </div>
      )}
      {!isCheckingSubscription && subscriptionStatus === 'unknown' && (
        <div role="status" style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10000, padding: '12px 18px', borderRadius: 12, background: 'var(--background-color, #fff)', color: 'var(--text-color, #222)', boxShadow: '0 2px 12px #0002' }}>
          Couldn’t verify your subscription. <button onClick={checkSubscription}>Retry</button>
        </div>
      )}
      {isCheckingSubscription ? (
        <LoadingScreen />
      ) : (
        isMobile ? (
          <MobileLayout
            // Chat state
            selectedChat={selectedChat}
            messages={messages}
            secondaryMessages={secondaryMessages}
            selectedModel={selectedModel}
            selectedModelName={selectedModelName}
            secondaryModel={secondaryModel}
            isDualMode={isDualMode}
            reasoningStream={reasoningStream}
            inputText={inputText}
            showSettings={showSettings}
            chatTitle={chatTitle}
            messageLimit={messageLimit}
            isLoadingMessages={isLoadingMessages}
            editingMessage={editingMessage}
            
            // Subscription state
            hasSubscription={hasSubscription}
            hasBasicSubscription={hasBasicSubscription}
            hasMaxSubscription={hasMaxSubscription}
            subscriptionStatus={subscriptionStatus}
            isCheckingSubscription={isCheckingSubscription}
            onRefreshSubscription={checkSubscription}
            subscriptionSources={subscriptionSources}
            
            // UI state
            isSidebarOpen={isSidebarOpen}
            chatWidth={chatWidth}
            artifactWidth={artifactWidth}
            currentArtifact={currentArtifact}
            isArtifactExpanded={isArtifactExpanded}
            particlesEnabled={particlesEnabled}
            setParticlesEnabled={setParticlesEnabled}
            
            // User data
            userName={userName}
            userEmail={userEmail}
            chats={chats}
            projects={projects}
            activeProject={activeProject}
            activeProjectId={activeProjectId}
            isLoadingProjects={isLoadingProjects}
            projectError={projectError}
            chatSidebarViewMode={chatSidebarViewMode}
            setChatSidebarViewMode={setChatSidebarViewMode}
            
            // Handlers
            toggleSidebar={toggleSidebar}
            onNewChat={wrappedHandleNewChat}
            onChatSelect={wrappedHandleChatSelect}
            onProjectSelect={wrappedHandleProjectSelect}
            onCreateProject={createProject}
            onUpdateProject={updateProject}
            onDeleteProject={deleteProject}
            onMoveChatToProject={wrappedMoveChatToProject}
            onUpgrade={handleUpgrade}
            onSettingsClick={wrappedHandleSettingsClick}
            onBackToChat={handleBackToChat}
            onLogout={handleLogout}
            onQuestionClick={handleQuestionClick}
            onArtifactOpen={wrappedHandleArtifactOpen}
            onArtifactClose={handleArtifactClose}
            onToggleArtifactExpanded={handleToggleArtifactExpanded}
            onResize={handleResize}
            onModelChange={handleModelChange}
            onPinnedModelChange={handlePinnedModelChange}
            onMessageSent={handleMessageSent}
            onSecondaryMessageSent={handleSecondaryMessageSent}
            updateChatTitle={updateChatTitle}
            onReasoningStreamUpdate={handleReasoningStreamUpdate}
            setInputText={setInputText}
            onStartEditMessage={startEditingMessage}
            onCancelEdit={cancelEditingMessage}
            applyEditAndTruncate={handleEditAndTruncate}
            realtimeVoice={realtimeVoice}
          />
        ) : (
          <div className="arrow-desktop-scale-stage theme-app-bg">
            <div className="arrow-desktop-scale-content flex h-full relative overflow-hidden">
              {particlesEnabled && <ParticleBackground shouldAnimate={shouldAnimateParticles} />}
              <FeatureAnnouncement features={getFeatureAnnouncementConfig()} />
              <DesktopLayout
                // Chat state
                selectedChat={selectedChat}
                messages={messages}
                secondaryMessages={secondaryMessages}
                selectedModel={selectedModel}
                selectedModelName={selectedModelName}
                secondaryModel={secondaryModel}
                isDualMode={isDualMode}
                setIsDualMode={setIsDualMode}
                setSecondaryModel={setSecondaryModel}
                reasoningStream={reasoningStream}
                inputText={inputText}
                showSettings={showSettings}
                chatTitle={chatTitle}
                messageLimit={messageLimit}
                isLoadingMessages={isLoadingMessages}
                editingMessage={editingMessage}
                
                // Subscription state
                hasSubscription={hasSubscription}
                hasBasicSubscription={hasBasicSubscription}
                hasMaxSubscription={hasMaxSubscription}
                subscriptionStatus={subscriptionStatus}
                isCheckingSubscription={isCheckingSubscription}
                onRefreshSubscription={checkSubscription}
                subscriptionSources={subscriptionSources}
                
                // UI state
                isSidebarOpen={isSidebarOpen}
                chatWidth={chatWidth}
                artifactWidth={artifactWidth}
                currentArtifact={currentArtifact}
                isArtifactExpanded={isArtifactExpanded}
                particlesEnabled={particlesEnabled}
                setParticlesEnabled={setParticlesEnabled}
                chatSidebarViewMode={chatSidebarViewMode}
                setChatSidebarViewMode={setChatSidebarViewMode}
                
                // User data
                userName={userName}
                userEmail={userEmail}
                chats={chats}
                projects={projects}
                activeProject={activeProject}
                activeProjectId={activeProjectId}
                isLoadingProjects={isLoadingProjects}
                projectError={projectError}
                
                // Handlers
                toggleSidebar={toggleSidebar}
                onNewChat={wrappedHandleNewChat}
                onChatSelect={wrappedHandleChatSelect}
                onProjectSelect={wrappedHandleProjectSelect}
                onCreateProject={createProject}
                onUpdateProject={updateProject}
                onDeleteProject={deleteProject}
                onMoveChatToProject={wrappedMoveChatToProject}
                onUpgrade={handleUpgrade}
                onSettingsClick={wrappedHandleSettingsClick}
                onBackToChat={handleBackToChat}
                onLogout={handleLogout}
                onQuestionClick={handleQuestionClick}
                onArtifactOpen={wrappedHandleArtifactOpen}
                onArtifactClose={handleArtifactClose}
                onToggleArtifactExpanded={handleToggleArtifactExpanded}
                onResize={handleResize}
                onModelChange={handleModelChange}
                onPinnedModelChange={handlePinnedModelChange}
                onMessageSent={handleMessageSent}
                onSecondaryMessageSent={handleSecondaryMessageSent}
                updateChatTitle={updateChatTitle}
                onReasoningStreamUpdate={handleReasoningStreamUpdate}
                setInputText={setInputText}
                onStartEditMessage={startEditingMessage}
                onCancelEdit={cancelEditingMessage}
                applyEditAndTruncate={handleEditAndTruncate}
                realtimeVoice={realtimeVoice}
              />
            </div>
          </div>
        )
      )}
    </AppLayout>
  );
};

export default MainChatInterface;
